import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";
import { fmtEurCompact as fmtEur } from "@/lib/formatMoney";

const emptyService = { name: "", color: "#3377FF", default_duration_min: 60, default_price: 0, description: "" };

const COLORS = [
  { name: "Grün", color: "#10B981" },
  { name: "Türkis", color: "#14B8A6" },
  { name: "Blau", color: "#3B82F6" },
  { name: "Indigo", color: "#4F46E5" },
  { name: "Violett", color: "#8B5CF6" },
  { name: "Rosa", color: "#EC4899" },
  { name: "Rot", color: "#EF4444" },
  { name: "Orange", color: "#F97316" },
  { name: "Gelb", color: "#F59E0B" },
  { name: "Braun", color: "#92400E" },
  { name: "Grau", color: "#64748B" },
  { name: "Schwarz", color: "#111827" },
];

export default function ServicesPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyService);

  const { data = [] } = useQuery({
    queryKey: ["services"],
    queryFn: async () => (await api.get("/services")).data,
  });

  const saveMut = useMutation({
    mutationFn: async () => editing
      ? (await api.put(`/services/${editing.id}`, form)).data
      : (await api.post("/services", form)).data,
    onSuccess: () => {
      toast.success(editing ? "Aktualisiert" : "Erstellt");
      qc.invalidateQueries({ queryKey: ["services"] });
      setOpen(false); setEditing(null); setForm(emptyService);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/services/${id}`)).data,
    onSuccess: () => { toast.success("Gelöscht"); qc.invalidateQueries({ queryKey: ["services"] }); },
  });

  const openNew = () => { setEditing(null); setForm(emptyService); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({
    name: s.name, color: s.color, default_duration_min: s.default_duration_min,
    default_price: s.default_price, description: s.description
  }); setOpen(true); };

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="flex flex-wrap items-center gap-2 mb-4 sm:mb-6">
        <div>
          <h1 className="font-display text-xl sm:text-3xl font-bold tracking-tight">Dienste</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Verwalten Sie Ihr Dienstleistungsangebot und deren Farbcodierung.</p>
        </div>
        <Button className="ml-auto" size="sm" onClick={openNew} data-testid="new-service-btn"><Plus className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline"> Neuer Dienst</span></Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.map((s) => (
          <div key={s.id} className="rounded-lg border border-border/60 bg-card p-4" data-testid={`service-card-${s.id}`}>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md" style={{ background: s.color }} />
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold truncate">{s.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{s.default_duration_min} Min · {fmtEur(s.default_price)} · {s.description || "—"}</div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => { if (confirm("Löschen?")) deleteMut.mutate(s.id); }} data-testid={`delete-service-${s.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Dienst bearbeiten" : "Neuer Dienst"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="service-name" /></div>
            <div>
              <Label>Farbe</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {COLORS.map((option) => (
                  <button key={option.color} type="button" onClick={() => setForm({...form, color: option.color})}
                    className={`h-8 w-8 rounded-md border border-black/10 ring-offset-2 ${form.color === option.color ? "ring-2 ring-primary" : ""}`}
                    style={{ background: option.color }} title={option.name} aria-label={option.name}
                    data-testid={`color-${option.color}`} />
                ))}
                <Input type="color" value={form.color} onChange={(e) => setForm({...form, color: e.target.value})} className="h-8 w-14 p-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Standarddauer (Minuten)</Label><Input type="number" value={form.default_duration_min} onChange={(e) => setForm({...form, default_duration_min: Number(e.target.value)})} data-testid="service-duration" /></div>
              <div><Label>Standardpreis (€)</Label><Input type="number" step="0.01" value={form.default_price} onChange={(e) => setForm({...form, default_price: Number(e.target.value)})} data-testid="service-price" /></div>
            </div>
            <div><Label>Beschreibung</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.name} data-testid="save-service">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
